import { computeNormalizedUnitCost, isPricePlausible, listAllocatableOptions } from './purchaseOptions.js';
import { assembleStoredComparison, selectAutomaticOption } from './priceComparison.js';
import { itemCountUnitPhysicalBasis, resolveNeedToPackageBasis, validatePackageConsistency } from './packageBasis.js';
// ORDER is not BILLING. A catch-weight line orders a case and is invoiced per
// pound, so its cost is an ESTIMATE — see billingBasis.js. Packages, received
// quantity and overage are untouched by it.
import { estimatedExtendedCost } from './billingBasis.js';
// ONE implementation, two runtimes (same contract as packageBasis.js): the
// item-replenishment target and the driver vocabulary come from the shared
// module the server trigger mirrors, so browser and DB name the same reason.
import { computePurchaseTarget, ORDER_DRIVERS } from '../../../supabase/functions/_shared/parAndMoq.mjs';

const toFiniteNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const roundMoney = (value) => Math.round(value * 100) / 100;
const round6 = (value) => Math.round(value * 1e6) / 1e6;

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const STALE_PRICE_DAYS = 90;

/**
 * Quantity still needed to reach PAR: max(0, par − onHand).
 * Tolerates string/null inputs (unparseable values count as 0).
 */
export function computeNeed(parLevel, onHand) {
  const par = toFiniteNumber(parLevel) || 0;
  const counted = toFiniteNumber(onHand) || 0;
  return Math.max(0, par - counted);
}

/**
 * Package math for a need quantity, in the vendor's legal ordering steps:
 *
 *   raw      = needQty / qtyPerPackage
 *   stepped  = ceil(raw / orderIncrement) × orderIncrement
 *   packages = max(MOQ, stepped)
 *
 * MOQ (the least the vendor sells), order increment (the steps they sell in)
 * and fractional COUNTING (whether a part-package can sit on a shelf) are
 * three different facts — none implies another. An unstated increment means
 * whole packages, except for cut-to-order vendors (`allowsFractional`), where
 * it means the vendor cuts whatever is asked. Mirrors orderPackagesForNeed
 * (_shared/parAndMoq.mjs); the parity test pins them together.
 *
 * @returns {{ packages: number, quantityReceived: number|null, overage: number|null,
 *             driver: string }}
 *   quantityReceived/overage are null when qtyPerPackage is not usable.
 *   `driver` names the decisive VENDOR-layer constraint (ORDER_DRIVERS),
 *   mirroring computeOrderPlan (_shared/parAndMoq.mjs): vendor_moq beats
 *   vendor_increment/package_rounding beats plain need; an unusable package
 *   size is 'unresolved'; zero need is 'none'. Item-level rules are applied
 *   by the CALLER (buildAllocationLine) — this function stays need-based.
 */
export function computePackages({ needQty, qtyPerPackage, moqPackages, allowsFractional = false, orderIncrement } = {}) {
  const need = toFiniteNumber(needQty) || 0;
  if (need <= 0) return { packages: 0, quantityReceived: 0, overage: 0, driver: ORDER_DRIVERS.NONE };

  const per = toFiniteNumber(qtyPerPackage);
  const canConvert = per !== null && per > 0;

  const incRaw = toFiniteNumber(orderIncrement);
  const increment = incRaw !== null && incRaw > 0 ? incRaw : allowsFractional ? null : 1;
  const snapUp = (value) => round6(Math.ceil(value / increment - 1e-9) * increment);

  const statedRaw = toFiniteNumber(moqPackages);
  const stated = statedRaw !== null && statedRaw > 0 ? statedRaw : null;
  const moq = increment === null
    ? (stated !== null ? Math.ceil(stated) : 0)
    : stated !== null
      ? snapUp(stated)
      : allowsFractional
        ? 0
        : snapUp(1);

  if (!canConvert) {
    // The minimum still gets ordered and billed; WHY is unresolved, not need.
    return { packages: Math.max(1, moq), quantityReceived: null, overage: null, driver: ORDER_DRIVERS.UNRESOLVED };
  }

  const exact = need / per;
  const stepped = increment === null ? round6(exact) : snapUp(exact);
  const packages = Math.max(moq, stepped);
  let driver = ORDER_DRIVERS.NEED;
  if (stepped > exact + 1e-9) {
    driver = incRaw !== null && incRaw > 0 && incRaw !== 1
      ? ORDER_DRIVERS.VENDOR_INCREMENT
      : ORDER_DRIVERS.PACKAGE_ROUNDING;
  }
  if (packages > stepped + 1e-9) driver = ORDER_DRIVERS.VENDOR_MOQ;
  const quantityReceived = round6(packages * per);
  const overage = round6(quantityReceived - need);
  return { packages, quantityReceived, overage, driver };
}

/**
 * Age of a price from its effective_date.
 *
 * @returns {{ ageDays: number|null, stale: boolean }}
 *   stale when older than 90 days. Unparseable dates → { ageDays: null, stale: false }.
 */
export function priceFreshness(effectiveDate, now = new Date()) {
  const then = Date.parse(effectiveDate);
  if (!Number.isFinite(then)) return { ageDays: null, stale: false };
  const nowTime = now instanceof Date ? now.getTime() : Date.parse(now);
  const ageDays = Math.floor((nowTime - then) / MS_PER_DAY);
  return { ageDays, stale: ageDays > STALE_PRICE_DAYS };
}

/**
 * Recomputes packages/costs/warnings for a need quantity against one option.
 * Shared by buildAllocationLine and recalculateLine.
 */
function priceForOption(quantityNeeded, option, countUnit = null, item = null, needBridge = null) {
  if (!option) {
    return {
      selected_purchase_option_id: null,
      // The package facts travel with the line so the review screen can label
      // "Order 7" as "7 cases" without re-looking-up the option. Null here for
      // the same reason every other field is: there is no package yet.
      purchase_unit: null,
      quantity_per_package: null,
      package_price: null,
      packages_required: null,
      quantity_received: null,
      overage: null,
      unit_cost: null,
      extended_cost: null,
      // Same keys on every path — an absent fact is null, never missing.
      cost_is_estimated: false,
      billing_basis_text: null,
      order_driver: ORDER_DRIVERS.UNRESOLVED,
      warnings: ['no_allocatable_option'],
    };
  }

  const warnings = [];
  // The one canonical package definition — confirmed interpretation, then
  // structured inner-pack columns, then the stored column. An option whose
  // stored facts conflict prices NOTHING: fabricating a total from one of the
  // disputed numbers is exactly the prorated-bag bug.
  const readinessItem = countUnit
    ? { ...(item || {}), unit: item?.unit ?? item?.count_unit ?? countUnit }
    : item;
  const consistency = validatePackageConsistency(option, { item: readinessItem, needBridge });
  const needIncompatible = consistency.warnings.includes('need_unit_incompatible_with_package_basis');
  if (consistency.status === 'conflict' || needIncompatible) {
    return {
      selected_purchase_option_id: option.id ?? null,
      purchase_unit: option.purchase_unit ?? null,
      quantity_per_package: null,
      package_price: toFiniteNumber(option.package_price),
      packages_required: null,
      quantity_received: null,
      overage: null,
      unit_cost: null,
      extended_cost: null,
      // Same keys on every path — an absent fact is null, never missing.
      cost_is_estimated: false,
      billing_basis_text: null,
      order_driver: ORDER_DRIVERS.UNRESOLVED,
      warnings: [
        'package_needs_review',
        ...(needIncompatible ? ['need_unit_incompatible_with_package_basis'] : []),
        ...consistency.warnings,
      ].filter((warning, index, all) => all.indexOf(warning) === index),
    };
  }
  const needBasis = countUnit
    ? resolveNeedToPackageBasis({
      needQty: quantityNeeded,
      needUnit: countUnit,
      option,
      packageBasis: consistency.basis,
      item: readinessItem,
      needBridge,
    })
    : null;
  if (needBasis && !needBasis.compatible) {
    return {
      selected_purchase_option_id: option.id ?? null,
      purchase_unit: option.purchase_unit ?? null,
      quantity_per_package: null,
      package_price: toFiniteNumber(option.package_price),
      packages_required: null,
      quantity_received: null,
      overage: null,
      unit_cost: null,
      extended_cost: null,
      // Same keys on every path — an absent fact is null, never missing.
      cost_is_estimated: false,
      billing_basis_text: null,
      order_driver: ORDER_DRIVERS.UNRESOLVED,
      warnings: ['package_needs_review', 'need_unit_incompatible_with_package_basis'],
    };
  }
  const quantityPerPackage = needBasis?.quantityPerPackageInNeedUnit
    ?? consistency.basis.quantityInInventoryUnit;
  const { packages, quantityReceived, overage, driver } = computePackages({
    needQty: quantityNeeded,
    qtyPerPackage: quantityPerPackage,
    moqPackages: option.moq_packages,
    allowsFractional: option.allows_fractional_packages === true,
    orderIncrement: option.order_increment,
  });

  const rawCost =
    toFiniteNumber(option.normalized_unit_cost) ?? computeNormalizedUnitCost(option);
  const unitCost = isPricePlausible(rawCost) ? rawCost : null;
  if (unitCost === null) warnings.push('missing_price');
  if (option.conversion_status === 'unresolved') warnings.push('unresolved_conversion');
  if (priceFreshness(option.effective_date).stale) warnings.push('stale_price');

  // ONE order-cost formula everywhere: packages × package price — the number
  // on the invoice. It used to be unit_cost × quantity_received here while
  // the optimization-save path wrote packages × package_price, and the two
  // disagree whenever the rounded unit cost drifts from price/qty. unit_cost
  // stays as display context; it no longer prices the line. Fallback to the
  // old formula only when the package price itself is missing.
  const packagePriceNum = toFiniteNumber(option.package_price);
  const exactCost = packagePriceNum !== null && packagePriceNum > 0 && packages !== null
    ? roundMoney(packages * packagePriceNum)
    : (unitCost !== null && quantityReceived !== null ? roundMoney(unitCost * quantityReceived) : null);

  // ── The one place ORDER and BILLING come apart ──────────────────────────
  //
  // A catch-weight vendor sells a CASE and invoices per POUND: the ribeye's
  // "4 PC / AVG 60 LB" case is $16.40 × ~60 LB ≈ $984, not the $16.40 that
  // packages × package_price produced. When the row proves that shape the
  // extended cost becomes that estimate and is MARKED as one. Every other row
  // — including every fixed package — takes the branch above, unchanged.
  //
  // The package arithmetic is deliberately not consulted here: packages,
  // quantity_received and overage keep coming from computePackages exactly as
  // before. Only the money differs, and only when it is provably different.
  const estimate = estimatedExtendedCost(option, packages, { item: readinessItem });
  const extendedCost = estimate ? estimate.amount : exactCost;
  if (estimate) warnings.push('cost_is_estimated');

  return {
    selected_purchase_option_id: option.id ?? null,
    purchase_unit: option.purchase_unit ?? null,
    quantity_per_package: quantityPerPackage,
    package_price: toFiniteNumber(option.package_price),
    packages_required: packages,
    quantity_received: quantityReceived,
    overage,
    unit_cost: unitCost,
    extended_cost: extendedCost,
    // An estimated total may never enter a figure presented as exact. These
    // two fields are how a caller labels it — "estimated — final invoice
    // varies with shipped weight" — and how a plan can subtotal the estimated
    // portion separately.
    cost_is_estimated: estimate !== null,
    billing_basis_text: estimate ? estimate.basisText : null,
    // The VENDOR-layer driver for the quantity handed in. buildAllocationLine
    // and recalculateLine combine it with the item-target driver.
    order_driver: driver,
    warnings,
  };
}

/**
 * Builds one allocation line for an inventory item, selecting the vendor the
 * same way a count line does: the lowest trustworthy comparable price under
 * the item's relationship comparison. When the comparison is unresolved the
 * line still allocates — the old preferred-first-then-cheapest order buys
 * sensibly while the relationship awaits review — but says so in
 * selection_reason and warnings instead of pretending it compared.
 *
 * The comparison facts ride on the line for the review screen and are frozen
 * into option_snapshot by the confirm RPC; purchaseAllocationService strips
 * them before insert (DISPLAY_ONLY_LINE_FIELDS).
 *
 * @param {{ item: Object, options: Array }} args
 *   item = { inventory_item_id, description, par, onHand, count_unit,
 *            comparison_* columns when the caller has them }
 */
export function buildAllocationLine({ item = {}, options = [] } = {}) {
  const quantityNeeded = computeNeed(item.par, item.onHand);
  // The restaurant's own purchase target: the raw need floored by the item's
  // replenishment minimum and stepped by its replenishment increment (both in
  // the count unit). The package arithmetic below pursues the TARGET, but
  // quantity_needed stays the RAW need — the server trigger reads it and
  // re-derives this same target, so the two must never be conflated.
  const targeting = computePurchaseTarget({
    rawNeed: quantityNeeded,
    itemMinimum: item.replenishment_minimum_qty,
    itemIncrement: item.replenishment_increment_qty,
  });
  const purchaseTarget = targeting.target ?? quantityNeeded;

  const readinessItem = { ...item, unit: item.unit ?? item.count_unit ?? null };
  // What one COUNT UNIT of this item physically contains, from the item's own
  // packages — lets a vendor package measured only in fl oz/lb cover a
  // container-unit need (0.8 case × 384 fl oz/case ÷ 192 fl oz/package = 2).
  const needBridge = itemCountUnitPhysicalBasis({ item: readinessItem, options });
  const allocatable = listAllocatableOptions(options);
  const orderableOptions = allocatable.filter(
    (option) => validatePackageConsistency(option, { item: readinessItem, needBridge }).orderReady,
  );
  const incompatibleOption = allocatable.find((option) =>
    validatePackageConsistency(option, { item: readinessItem, needBridge }).warnings
      .includes('need_unit_incompatible_with_package_basis')) || null;

  const comparison = assembleStoredComparison(item, options);
  const auto = selectAutomaticOption({ options: orderableOptions, comparison });
  let selected = auto.optionId != null
    ? orderableOptions.find((option) => String(option?.id) === String(auto.optionId)) || null
    : null;
  let selectionReason = selected ? auto.reason : null;
  let comparisonUnreviewed = false;

  if (!selected) {
    selected = orderableOptions[0] || null;
    if (selected) {
      const activeCount = orderableOptions.filter((o) => o && o.status === 'active').length;
      if (selected.is_preferred === true) selectionReason = 'preferred_fallback';
      else if (activeCount === 1) selectionReason = 'only';
      else {
        selectionReason = 'unresolved';
        comparisonUnreviewed = true;
      }
    } else {
      selectionReason = 'unresolved';
    }
  }

  const entry = selected
    ? comparison.options.find((e) => String(e.optionId) === String(selected.id)) || null
    : null;
  const priced = priceForOption(purchaseTarget, selected, item.count_unit ?? item.unit ?? null, readinessItem, needBridge);
  // Extra is measured against the RAW need — what arrives beyond the actual
  // shortage — matching what the confirm trigger freezes as `overage`
  // (overage_vs_target is a separate snapshot field).
  if (priced.quantity_received != null) {
    priced.overage = round6(priced.quantity_received - quantityNeeded);
  }
  if (!selected && incompatibleOption) {
    priced.warnings = [
      ...(priced.warnings || []),
      'package_needs_review',
      'need_unit_incompatible_with_package_basis',
    ].filter((warning, index, all) => all.indexOf(warning) === index);
  }
  if (comparisonUnreviewed) priced.warnings = [...(priced.warnings || []), 'comparison_needs_review'];

  return {
    inventory_item_id: item.inventory_item_id ?? null,
    description: item.description ?? null,
    quantity_needed: quantityNeeded,
    count_unit: item.count_unit ?? null,
    selection_reason: selectionReason,
    comparison_unit: comparison.comparisonUnit ?? null,
    comparison_quantity_per_package: entry?.quantityPerPurchasePackage ?? null,
    comparable_unit_cost: entry?.comparableUnitCost ?? null,
    comparison_source: entry?.source ?? null,
    comparison_confidence: entry?.confidence ?? null,
    comparison_estimated: entry?.estimated === true,
    ...priced,
    // The layered decision, named: raw need beside the target it became, and
    // the ONE decisive constraint. The vendor layer names itself when it
    // raised the buy; otherwise the item layer's driver stands.
    raw_need_quantity: quantityNeeded,
    purchase_target_quantity: targeting.target,
    target_driver: targeting.driver,
    order_driver: priced.order_driver === ORDER_DRIVERS.NEED ? targeting.driver : priced.order_driver,
  };
}

/**
 * Re-prices an existing line after the user switches purchase option.
 * Item identity, need, and count unit are carried over unchanged.
 *
 * When the line carries the item's replenishment fields
 * (replenishment_minimum_qty / replenishment_increment_qty — see
 * updateLineOption, which fetches them), the same item-target stage as
 * buildAllocationLine applies, so a vendor switch stores the packages number
 * the server trigger will recompute. Without them the target is the raw need.
 *
 * `itemOptions` is the item's OWN option list, from which the count unit's
 * physical definition is derived — the same `needBridge` buildAllocationLine
 * builds. A manual switch must be judged by exactly the rules the automatic
 * selection was: without the bridge, a container-unit need (0.9 BIB, 3.5
 * bottles) that the automatic pass covered through contents came back
 * `need_unit_incompatible_with_package_basis` the moment somebody chose the
 * same vendor by hand. Absent (a caller that cannot fetch them), behaviour is
 * exactly as before.
 */
export function recalculateLine(line, option, { itemOptions = null, item = null } = {}) {
  const rawNeed = toFiniteNumber(line?.quantity_needed) || 0;
  const targeting = computePurchaseTarget({
    rawNeed,
    itemMinimum: line?.replenishment_minimum_qty,
    itemIncrement: line?.replenishment_increment_qty,
  });
  const purchaseTarget = targeting.target ?? rawNeed;
  const lineItem = line?.count_unit || item
    ? { ...(item || {}), id: item?.id ?? line?.inventory_item_id ?? null, unit: item?.unit ?? line?.count_unit ?? null }
    : null;
  const needBridge = lineItem && Array.isArray(itemOptions)
    ? itemCountUnitPhysicalBasis({ item: lineItem, options: itemOptions })
    : null;
  const priced = priceForOption(
    purchaseTarget,
    option,
    line?.count_unit ?? null,
    lineItem,
    needBridge,
  );
  if (priced.quantity_received != null) {
    priced.overage = round6(priced.quantity_received - rawNeed);
  }
  return {
    ...line,
    ...priced,
    raw_need_quantity: rawNeed,
    purchase_target_quantity: targeting.target,
    target_driver: targeting.driver,
    order_driver: priced.order_driver === ORDER_DRIVERS.NEED ? targeting.driver : priced.order_driver,
  };
}

/**
 * Groups allocation lines by vendor. Lines whose selected option is missing
 * (or has no vendor) land in the `null` (unassigned) group.
 *
 * @param {Array} lines
 * @param {Map|Object} optionsById - purchase option lookup by id
 * @returns {Map<string|null, Array>}
 */
export function groupLinesByVendor(lines, optionsById) {
  const lookup = (id) => {
    if (id === null || id === undefined) return null;
    if (optionsById instanceof Map) return optionsById.get(id) || null;
    return optionsById?.[id] || null;
  };

  const groups = new Map();
  for (const line of lines || []) {
    const option = lookup(line?.selected_purchase_option_id);
    const vendorId = option?.vendor_id ?? null;
    if (!groups.has(vendorId)) groups.set(vendorId, []);
    groups.get(vendorId).push(line);
  }
  return groups;
}
